import { useMemo, useState } from "react"
import { LuExternalLink } from "react-icons/lu"
import { Box, CloseButton, Dialog, HStack, Portal, Stack } from "@chakra-ui/react"
import { Employee, MatchType } from "@/types"
import { confirmLinkedinMatch } from "@/api"
import { Button, Text, toaster } from "../ui"

const MATCH_LABELS: Record<MatchType, { label: string; color: string; bg: string }> = {
  exact: { label: "Exact Match", color: "green.700", bg: "green.100" },
  prefix: { label: "Likely Match", color: "blue.700", bg: "blue.100" },
  nickname: { label: "Nickname Match", color: "orange.700", bg: "orange.100" },
  lastNameOnly: { label: "Last Name Only", color: "gray.600", bg: "gray.100" },
}

interface Props {
  isOpen: boolean
  onClose: () => void
  matches: Employee[]
  dealId: number
  clientId: number
  firstName: string
  lastName: string
}

export const EmployeeMatchModal = ({
  isOpen,
  onClose,
  matches,
  dealId,
  clientId,
  firstName,
  lastName,
}: Props) => {
  const [isConfirming, setIsConfirming] = useState(false)

  // Deduplicate matches by linkedinUrl (same person can appear multiple times with different job entries)
  const uniqueMatches = useMemo(() => {
    const seen = new Set<string>()
    return matches.filter((m) => {
      if (seen.has(m.linkedinUrl)) return false
      seen.add(m.linkedinUrl)
      return true
    })
  }, [matches])

  const handleConfirm = async (match: Employee) => {
    setIsConfirming(true)
    try {
      await confirmLinkedinMatch({
        clientId,
        linkedinUrl: match.linkedinUrl,
        dealId,
        firstName,
        lastName,
      })
      toaster.success({ title: "LinkedIn profile linked successfully" })
      onClose()
    } catch {
      toaster.error({ title: "Failed to link LinkedIn profile" })
    } finally {
      setIsConfirming(false)
    }
  }

  return (
    <Dialog.Root size="lg" open={isOpen} onOpenChange={(e) => !e.open && onClose()}>
      <Portal>
        <Dialog.Backdrop />
        <Dialog.Positioner>
          <Dialog.Content bg="card.bg">
            <Dialog.Header px={6} pt={5} pb={2}>
              <Dialog.Title fontFamily="body" fontWeight="semibold">
                Potential LinkedIn Match Found
              </Dialog.Title>
            </Dialog.Header>
            <Dialog.Body px={6} pb={6}>
              <Text fontSize="sm" color="text.muted" mb={4}>
                We found {uniqueMatches.length} employee{uniqueMatches.length > 1 ? "s" : ""} with a matching last name. Select one to link their LinkedIn profile.
              </Text>

              <Stack gap={3} maxH="400px" overflowY="auto">
                {uniqueMatches.map((match) => {
                  const matchInfo = match.matchType ? MATCH_LABELS[match.matchType] : null
                  return (
                    <Box
                      key={match.id}
                      p={4}
                      border="1px solid"
                      borderColor={match.matchType === "exact" ? "green.300" : "border.default"}
                      borderRadius="md"
                      bg={match.matchType === "exact" ? "green.50" : undefined}
                    >
                      <HStack justifyContent="space-between" alignItems="flex-start">
                        <Stack gap={1} flex={1}>
                          <HStack gap={2}>
                            <Text fontWeight="semibold">{match.name}</Text>
                            {matchInfo && (
                              <Box
                                px={2}
                                py={0.5}
                                borderRadius="full"
                                bg={matchInfo.bg}
                              >
                                <Text fontSize="xs" fontWeight="medium" color={matchInfo.color}>
                                  {matchInfo.label}
                                </Text>
                              </Box>
                            )}
                          </HStack>
                          {match.jobTitle && (
                            <Text fontSize="sm" color="text.muted">{match.jobTitle}</Text>
                          )}
                          {match.location && (
                            <Text fontSize="xs" color="text.muted">{match.location}</Text>
                          )}
                          {match.liStartDateFormatted && (
                            <Text fontSize="xs" color="text.muted">
                              Started: {match.liStartDateFormatted}
                              {match.liEndDateFormatted && ` — Left: ${match.liEndDateFormatted}`}
                            </Text>
                          )}
                          <HStack gap={1} mt={1}>
                            <a
                              href={match.linkedinUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{ display: "inline-flex", alignItems: "center", gap: "4px" }}
                            >
                              <Text fontSize="xs" color="blue.500" _hover={{ textDecoration: "underline" }}>
                                {match.linkedinUrl}
                              </Text>
                              <LuExternalLink size={12} color="#3182ce" />
                            </a>
                          </HStack>
                        </Stack>
                        <Button
                          size="sm"
                          disabled={isConfirming}
                          loading={isConfirming}
                          onClick={() => handleConfirm(match)}
                        >
                          Confirm Match
                        </Button>
                      </HStack>
                    </Box>
                  )
                })}
              </Stack>

              <Button
                mt={4}
                w="100%"
                variant="outline"
                onClick={onClose}
                disabled={isConfirming}
              >
                Skip
              </Button>
            </Dialog.Body>
            <Dialog.CloseTrigger asChild>
              <CloseButton size="sm" />
            </Dialog.CloseTrigger>
          </Dialog.Content>
        </Dialog.Positioner>
      </Portal>
    </Dialog.Root>
  )
}
